import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eventQuotesAPI, eventInvoicesAPI, eventInvoicePaymentsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime, formatDate, cn } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Calendar,
  Users,
  MapPin,
  Mail,
  Phone,
  Building2,
  Loader2,
  Plus,
  Trash2,
  DollarSign,
  FileText,
  CreditCard,
  Download,
  Send,
  Check,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  CLINIC_NAME, CLINIC_ADDRESS, CLINIC_EMAIL,
  BUSINESS_PHONES_INLINE,
} from '../config/clinicContacts';

const LOGO_URL = "/logo.png"; // Local logo file to avoid CORS issues

// Helper to load image as base64
const loadImageAsBase64 = async (url) => {
  try {
    const absoluteUrl = url.startsWith('/') ? window.location.origin + url : url;
    const response = await fetch(absoluteUrl, { mode: 'cors' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to load logo:', error);
    return null;
  }
};

export default function EventQuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  
  const [quote, setQuote] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Invoice form state
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    bill_to_business: '',
    bill_to_contact: '',
    bill_to_email: '',
    bill_to_phone: '',
    invoice_date: '',
    due_date: '',
    currency: 'XCD',
    line_items: [{ description: '', quantity: 1, unit_price: 0 }],
    discount: 0,
    tax_rate: 0,
    notes: '',
    terms: 'Payment due within 14 days of invoice date.',
  });
  const [savingInvoice, setSavingInvoice] = useState(false);
  
  // Payment form state
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    payment_date: '',
    amount: 0,
    method: 'bank_transfer',
    reference: '',
    notes: '',
  });
  const [savingPayment, setSavingPayment] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [quotesRes, invoicesRes, summaryRes] = await Promise.all([
        eventQuotesAPI.getAll({}),
        eventInvoicesAPI.getByQuote(id),
        eventQuotesAPI.getFinancialSummary(id),
      ]);
      
      const foundQuote = quotesRes.data.find(q => q.id === id);
      setQuote(foundQuote);
      setInvoices(invoicesRes.data);
      setFinancialSummary(summaryRes.data);
      
      // Pre-fill invoice form
      if (foundQuote) {
        const today = new Date().toISOString().split('T')[0];
        const dueDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        setInvoiceForm(prev => ({
          ...prev,
          bill_to_business: foundQuote.business_name,
          bill_to_contact: foundQuote.contact_name,
          bill_to_email: foundQuote.email,
          bill_to_phone: foundQuote.phone,
          invoice_date: today,
          due_date: dueDate,
        }));
      }
    } catch (err) {
      toast.error('Failed to load quote details');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    try {
      await eventQuotesAPI.updateStatus(id, newStatus);
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const addLineItem = () => {
    setInvoiceForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, unit_price: 0 }],
    }));
  };

  const removeLineItem = (index) => {
    setInvoiceForm(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index),
    }));
  };

  const updateLineItem = (index, field, value) => {
    setInvoiceForm(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const calculateSubtotal = () => {
    return invoiceForm.line_items.reduce((sum, item) => 
      sum + (item.quantity * item.unit_price), 0
    );
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = subtotal * (invoiceForm.tax_rate / 100);
    return subtotal - invoiceForm.discount + tax;
  };

  const handleCreateInvoice = async () => {
    if (invoiceForm.line_items.some(item => !item.description || item.unit_price <= 0)) {
      toast.error('Please fill in all line items with valid amounts');
      return;
    }

    setSavingInvoice(true);
    try {
      await eventInvoicesAPI.create({
        quote_request_id: id,
        ...invoiceForm,
      });
      toast.success('Invoice created successfully');
      setShowInvoiceDialog(false);
      setInvoiceForm(prev => ({
        ...prev,
        line_items: [{ description: '', quantity: 1, unit_price: 0 }],
        discount: 0,
        tax_rate: 0,
        notes: '',
      }));
      fetchData();
    } catch (err) {
      toast.error('Failed to create invoice');
    }
    setSavingInvoice(false);
  };

  const handleAddPayment = async () => {
    if (paymentForm.amount <= 0) {
      toast.error('Payment amount must be greater than 0');
      return;
    }

    setSavingPayment(true);
    try {
      await eventInvoicePaymentsAPI.create({
        invoice_id: selectedInvoice.id,
        ...paymentForm,
      });
      toast.success('Payment recorded successfully');
      setShowPaymentDialog(false);
      setPaymentForm({
        payment_date: new Date().toISOString().split('T')[0],
        amount: 0,
        method: 'bank_transfer',
        reference: '',
        notes: '',
      });
      fetchData();
    } catch (err) {
      toast.error('Failed to record payment');
    }
    setSavingPayment(false);
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentForm(prev => ({
      ...prev,
      amount: invoice.total - invoice.amount_paid,
    }));
    setShowPaymentDialog(true);
  };

  const generateInvoicePDF = async (invoice) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Try to add logo
    try {
      const logoBase64 = await loadImageAsBase64(LOGO_URL);
      if (logoBase64) {
        doc.addImage(logoBase64, 'PNG', 15, 8, 25, 25);
      }
    } catch (e) {
      console.warn('Could not load logo for PDF');
    }
    
    // Header with clinic info
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(CLINIC_NAME, 45, 18);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(CLINIC_ADDRESS, 45, 24);
    doc.text(`Tel: ${BUSINESS_PHONES_INLINE}`, 45, 29);
    doc.text(`Email: ${CLINIC_EMAIL}`, 45, 34);
    
    // Invoice title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('INVOICE', pageWidth - 15, 18, { align: 'right' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(invoice.invoice_number, pageWidth - 15, 25, { align: 'right' });
    
    // Divider line
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(15, 40, pageWidth - 15, 40);
    
    // Invoice details
    let yPos = 48;
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text(`Invoice Date: ${formatDate(invoice.invoice_date)}`, 20, yPos);
    doc.text(`Due Date: ${formatDate(invoice.due_date)}`, 20, yPos + 6);
    doc.text(`Status: ${invoice.status.toUpperCase()}`, 20, yPos + 12);
    
    // Bill To
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Bill To:', 120, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(invoice.bill_to_business, 120, yPos + 6);
    doc.text(invoice.bill_to_contact, 120, yPos + 12);
    doc.text(invoice.bill_to_email, 120, yPos + 18);
    doc.text(invoice.bill_to_phone, 120, yPos + 24);
    
    // Line items table
    const tableData = invoice.line_items.map(item => [
      item.description,
      item.quantity.toString(),
      `${invoice.currency} ${item.unit_price.toFixed(2)}`,
      `${invoice.currency} ${item.line_total.toFixed(2)}`,
    ]);
    
    autoTable(doc, {
      startY: 85,
      head: [['Description', 'Qty', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [15, 23, 42] },
      styles: { fontSize: 9 },
      margin: { left: 15, right: 15 },
    });
    
    // Totals
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setTextColor(60, 60, 60);
    doc.text(`Subtotal: ${invoice.currency} ${invoice.subtotal.toFixed(2)}`, 140, finalY);
    if (invoice.discount > 0) {
      doc.text(`Discount: ${invoice.currency} ${invoice.discount.toFixed(2)}`, 140, finalY + 6);
    }
    if (invoice.tax_amount > 0) {
      doc.text(`Tax (${invoice.tax_rate}%): ${invoice.currency} ${invoice.tax_amount.toFixed(2)}`, 140, finalY + 12);
    }
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(`Total: ${invoice.currency} ${invoice.total.toFixed(2)}`, 140, finalY + 20);
    doc.text(`Amount Paid: ${invoice.currency} ${invoice.amount_paid.toFixed(2)}`, 140, finalY + 26);
    doc.text(`Balance Due: ${invoice.currency} ${(invoice.total - invoice.amount_paid).toFixed(2)}`, 140, finalY + 32);
    
    // Notes
    if (invoice.notes) {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      doc.text('Notes:', 20, finalY + 45);
      doc.setFontSize(9);
      const noteLines = doc.splitTextToSize(invoice.notes, 170);
      doc.text(noteLines, 20, finalY + 51);
    }
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Thank you for choosing ${CLINIC_NAME}`, pageWidth / 2, 280, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
    
    doc.save(`Invoice_${invoice.invoice_number}.pdf`);
    toast.success('Invoice PDF downloaded');
  };

  const generateInvoiceSummaryPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Try to add logo
    try {
      const logoBase64 = await loadImageAsBase64(LOGO_URL);
      if (logoBase64) {
        doc.addImage(logoBase64, 'PNG', 15, 8, 25, 25);
      }
    } catch (e) {
      console.warn('Could not load logo for PDF');
    }
    
    // Header with clinic info
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(CLINIC_NAME, 45, 18);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(CLINIC_ADDRESS, 45, 24);
    doc.text(`Tel: ${BUSINESS_PHONES_INLINE}`, 45, 29);
    doc.text(`Email: ${CLINIC_EMAIL}`, 45, 34);
    
    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('INVOICE SUMMARY', pageWidth - 15, 18, { align: 'right' });
    
    // Divider line
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(15, 40, pageWidth - 15, 40);
    
    // Event/Client info
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(`Event: ${quote?.event_type || 'Event'}`, 20, 48);
    doc.text(`Client: ${quote?.business_name || 'Client'}`, 20, 54);
    
    // Financial Summary Box
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(15, 62, pageWidth - 30, 25, 3, 3, 'F');
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Financial Summary', 20, 72);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(`Total Invoiced: XCD ${financialSummary?.total_invoiced?.toFixed(2) || '0.00'}`, 20, 80);
    doc.text(`Total Paid: XCD ${financialSummary?.total_paid?.toFixed(2) || '0.00'}`, 80, 80);
    doc.setFont('helvetica', 'bold');
    doc.text(`Balance Due: XCD ${financialSummary?.balance_due?.toFixed(2) || '0.00'}`, 140, 80);
    
    // Invoices table
    const tableData = invoices.map(inv => [
      inv.invoice_number,
      formatDate(inv.invoice_date),
      formatDate(inv.due_date),
      `XCD ${inv.total.toFixed(2)}`,
      `XCD ${inv.amount_paid.toFixed(2)}`,
      `XCD ${(inv.total - inv.amount_paid).toFixed(2)}`,
      inv.status.toUpperCase(),
    ]);
    
    autoTable(doc, {
      startY: 95,
      head: [['Invoice #', 'Date', 'Due Date', 'Total', 'Paid', 'Balance', 'Status']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [15, 23, 42], fontSize: 8 },
      styles: { fontSize: 8 },
      margin: { left: 15, right: 15 },
    });
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Thank you for choosing ${CLINIC_NAME}`, pageWidth / 2, 280, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
    
    doc.save(`Invoice_Summary_${quote?.business_name?.replace(/\s+/g, '_') || 'Event'}.pdf`);
    toast.success('Invoice summary PDF downloaded');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-50 text-amber-600 border border-amber-200',
      reviewing: 'bg-blue-50 text-blue-600 border border-blue-200',
      quoted: 'bg-purple-50 text-purple-600 border border-purple-200',
      accepted: 'bg-emerald-50 text-emerald-600 border border-emerald-200',
      declined: 'bg-red-50 text-red-600 border border-red-200',
    };
    return styles[status] || 'bg-slate-100 text-slate-600 border border-slate-200';
  };

  const getInvoiceStatusBadge = (status) => {
    const styles = {
      unpaid: { bg: 'bg-slate-500/10', text: 'text-slate-400', icon: Clock },
      partial: { bg: 'bg-amber-500/10', text: 'text-amber-600', icon: AlertCircle },
      paid: { bg: 'bg-emerald-500/10', text: 'text-emerald-600', icon: Check },
      overdue: { bg: 'bg-red-500/10', text: 'text-red-600', icon: AlertCircle },
    };
    return styles[status] || styles.unpaid;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">Quote Not Found</h2>
        <Button onClick={() => navigate('/app/event-quotes')}>Back to Event Quotes</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn p-2" data-testid="event-quote-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/app/event-quotes')}
            className="rounded-xl hover:bg-slate-50"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <p className="text-sm font-medium text-primary mb-1">Event Quote</p>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">{quote.business_name}</h1>
            <p className="text-slate-500 mt-1">{quote.event_type} • {quote.reference_number}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={quote.status} onValueChange={handleStatusChange}>
            <SelectTrigger className={cn("w-32 rounded-xl", getStatusBadge(quote.status))}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="reviewing">Reviewing</SelectItem>
              <SelectItem value="quoted">Quoted</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setShowInvoiceDialog(true)} className="gap-2 rounded-full btn-primary-glow">
            <Plus className="h-4 w-4" />
            Create Invoice
          </Button>
        </div>
      </div>

      {/* Financial Summary Card */}
      {financialSummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="premium-card-hover p-5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-blue-600" strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900">{financialSummary.invoice_count}</p>
              <p className="text-xs text-slate-500">Invoices</p>
            </div>
          </div>
          <div className="premium-card-hover p-5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-20 h-20 bg-purple-500/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <DollarSign className="h-4 w-4 text-purple-600" strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900">XCD {financialSummary.total_invoiced.toFixed(2)}</p>
              <p className="text-xs text-slate-500">Total Invoiced</p>
            </div>
          </div>
          <div className="premium-card-hover p-5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Check className="h-4 w-4 text-emerald-600" strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-2xl font-bold text-emerald-600">XCD {financialSummary.total_paid.toFixed(2)}</p>
              <p className="text-xs text-slate-500">Total Paid</p>
            </div>
          </div>
          <div className="premium-card-hover p-5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-20 h-20 bg-orange-500/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center">
                  <AlertCircle className="h-4 w-4 text-orange-600" strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-2xl font-bold text-orange-600">XCD {financialSummary.balance_due.toFixed(2)}</p>
              <p className="text-xs text-slate-500">Balance Due</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="premium-card overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <TabsList className="bg-slate-50">
              <TabsTrigger value="overview" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-lg">Overview</TabsTrigger>
              <TabsTrigger value="quote" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-lg">Quote Details</TabsTrigger>
              <TabsTrigger value="invoices" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-lg">Invoices ({invoices.length})</TabsTrigger>
              <TabsTrigger value="summary" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-lg">Invoice Summary</TabsTrigger>
            </TabsList>
          </div>

          <div className="p-5">
            <TabsContent value="overview" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="premium-card-hover p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Contact Information</h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-xl bg-slate-50">
                        <Building2 className="h-5 w-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{quote.business_name}</p>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Business Name</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-xl bg-slate-50">
                        <Users className="h-5 w-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{quote.contact_name}</p>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Contact Person</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-xl bg-slate-50">
                        <Mail className="h-5 w-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{quote.email}</p>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Email</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-xl bg-slate-50">
                        <Phone className="h-5 w-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{quote.phone}</p>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Phone</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="premium-card-hover p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Event Details</h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-xl bg-slate-50">
                        <Calendar className="h-5 w-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{quote.event_dates}</p>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Event Dates ({quote.event_duration})</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-xl bg-slate-50">
                        <MapPin className="h-5 w-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{quote.event_location}</p>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Location</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-xl bg-slate-50">
                        <Users className="h-5 w-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{quote.expected_attendees} attendees</p>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Expected Attendance</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={cn("rounded-lg", getStatusBadge(quote.status))}>{quote.status}</Badge>
                      <span className="text-sm text-slate-500">Coverage: {quote.coverage_level}</span>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="quote" className="mt-0">
              <div className="premium-card-hover p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-900">Quote Request Details</h3>
                  <p className="text-sm text-slate-500">Submitted {formatDateTime(quote.submitted_at)}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Event Type</Label>
                    <p className="font-medium text-slate-900">{quote.event_type}</p>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Coverage Level</Label>
                    <p className="font-medium text-slate-900">{quote.coverage_level}</p>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reference Number</Label>
                    <code className="font-mono text-sm text-primary bg-slate-50 px-2 py-0.5 rounded-lg">{quote.reference_number}</code>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</Label>
                    <p><Badge className={cn("rounded-lg", getStatusBadge(quote.status))}>{quote.status}</Badge></p>
                  </div>
                </div>
              {quote.additional_notes && (
                <div className="mt-4">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Additional Notes</Label>
                  <p className="mt-1 p-4 bg-slate-50 rounded-xl text-slate-900">{quote.additional_notes}</p>
                </div>
              )}
              </div>
            </TabsContent>

            <TabsContent value="invoices" className="mt-0">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Invoices</h3>
                  <p className="text-sm text-slate-500">Manage invoices and payments for this event</p>
                </div>
                <Button onClick={() => setShowInvoiceDialog(true)} size="sm" className="gap-2 rounded-full btn-primary-glow">
                  <Plus className="h-4 w-4" />
                  New Invoice
                </Button>
              </div>
              
              {invoices.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-4">
                    <FileText className="h-8 w-8 text-slate-500" strokeWidth={1.5} />
                  </div>
                  <p className="text-slate-500">No invoices yet. Create one to get started.</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <table className="premium-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Invoice #</th>
                        <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Date</th>
                        <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Due Date</th>
                        <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Total</th>
                        <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Paid</th>
                        <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Balance</th>
                        <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                        <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice) => {
                        const statusStyle = getInvoiceStatusBadge(invoice.status);
                        const StatusIcon = statusStyle.icon;
                        return (
                          <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                            <td className="px-5 py-4">
                              <code className="text-sm bg-slate-50 px-2 py-0.5 rounded-lg font-mono text-primary">{invoice.invoice_number}</code>
                            </td>
                            <td className="px-5 py-4 text-slate-500">{formatDate(invoice.invoice_date)}</td>
                            <td className="px-5 py-4 text-slate-500">{formatDate(invoice.due_date)}</td>
                            <td className="px-5 py-4 text-right font-medium text-slate-900">{invoice.currency} {invoice.total.toFixed(2)}</td>
                            <td className="px-5 py-4 text-right text-emerald-600">{invoice.currency} {invoice.amount_paid.toFixed(2)}</td>
                            <td className="px-5 py-4 text-right text-orange-600">{invoice.currency} {(invoice.total - invoice.amount_paid).toFixed(2)}</td>
                            <td className="px-5 py-4">
                              <Badge className={cn(statusStyle.bg, statusStyle.text, 'gap-1 rounded-lg')}>
                                <StatusIcon className="h-3 w-3" />
                                {invoice.status}
                              </Badge>
                            </td>
                            <td className="px-5 py-4 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="rounded-lg hover:bg-slate-50" 
                                  onClick={() => openPaymentDialog(invoice)}
                                  disabled={invoice.status === 'paid'}
                                >
                                  <CreditCard className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" className="rounded-lg hover:bg-slate-50" onClick={() => generateInvoicePDF(invoice)}>
                                  <Download className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </ScrollArea>
              )}
            </TabsContent>

            <TabsContent value="summary" className="mt-0">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Invoice Summary</h3>
                  <p className="text-sm text-slate-500">Overview of all invoices for this event</p>
                </div>
                <Button onClick={generateInvoiceSummaryPDF} className="gap-2 rounded-full btn-primary-glow">
                  <Download className="h-4 w-4" />
                  Generate Summary PDF
                </Button>
              </div>
              
              <div className="space-y-6">
                {/* Summary stats */}
                <div className="grid grid-cols-3 gap-4 p-5 bg-slate-50 rounded-xl border border-slate-100">
                  <div className="text-center">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Total Invoiced</p>
                    <p className="text-xl font-bold text-slate-900 mt-1">XCD {financialSummary?.total_invoiced?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Total Paid</p>
                    <p className="text-xl font-bold text-emerald-600 mt-1">XCD {financialSummary?.total_paid?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Balance Due</p>
                    <p className="text-xl font-bold text-orange-600 mt-1">XCD {financialSummary?.balance_due?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>

                {/* Invoice list */}
                {invoices.length > 0 && (
                  <table className="premium-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Invoice</th>
                        <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Date</th>
                        <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Due</th>
                        <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Total</th>
                        <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Paid</th>
                        <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Balance</th>
                        <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                          <td className="px-5 py-4"><code className="text-sm bg-slate-50 px-2 py-0.5 rounded-lg font-mono text-primary">{inv.invoice_number}</code></td>
                          <td className="px-5 py-4 text-slate-500">{formatDate(inv.invoice_date)}</td>
                          <td className="px-5 py-4 text-slate-500">{formatDate(inv.due_date)}</td>
                          <td className="px-5 py-4 text-right text-slate-900">XCD {inv.total.toFixed(2)}</td>
                          <td className="px-5 py-4 text-right text-emerald-600">XCD {inv.amount_paid.toFixed(2)}</td>
                          <td className="px-5 py-4 text-right text-orange-600">XCD {(inv.total - inv.amount_paid).toFixed(2)}</td>
                          <td className="px-5 py-4">
                            <Badge className={cn(getInvoiceStatusBadge(inv.status).bg, getInvoiceStatusBadge(inv.status).text, "rounded-lg")}>
                              {inv.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </TabsContent>
          </div>
        </div>
      </Tabs>

      {/* Create Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Create Invoice</DialogTitle>
            <DialogDescription>Create a new invoice for {quote.business_name}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Bill To */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Business Name</Label>
                <Input 
                  value={invoiceForm.bill_to_business} 
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, bill_to_business: e.target.value }))}
                  className="mt-1 rounded-xl bg-white border-slate-200"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Contact Person</Label>
                <Input 
                  value={invoiceForm.bill_to_contact} 
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, bill_to_contact: e.target.value }))}
                  className="mt-1 rounded-xl bg-white border-slate-200"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</Label>
                <Input 
                  type="email"
                  value={invoiceForm.bill_to_email} 
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, bill_to_email: e.target.value }))}
                  className="mt-1 rounded-xl bg-white border-slate-200"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Phone</Label>
                <Input 
                  value={invoiceForm.bill_to_phone} 
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, bill_to_phone: e.target.value }))}
                  className="mt-1 rounded-xl bg-white border-slate-200"
                />
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Invoice Date</Label>
                <Input 
                  type="date"
                  value={invoiceForm.invoice_date} 
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, invoice_date: e.target.value }))}
                  className="mt-1 rounded-xl bg-white border-slate-200"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Due Date</Label>
                <Input 
                  type="date"
                  value={invoiceForm.due_date} 
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, due_date: e.target.value }))}
                  className="mt-1 rounded-xl bg-white border-slate-200"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Currency</Label>
                <Select value={invoiceForm.currency} onValueChange={(v) => setInvoiceForm(prev => ({ ...prev, currency: v }))}>
                  <SelectTrigger className="mt-1 rounded-xl bg-white border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="XCD">XCD</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Line Items</Label>
                <Button type="button" className="rounded-full bg-slate-50 text-slate-900 hover:bg-slate-100 border border-slate-200" size="sm" onClick={addLineItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {invoiceForm.line_items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <Input 
                      placeholder="Description"
                      className="flex-1"
                      value={item.description}
                      onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                    />
                    <Input 
                      type="number"
                      placeholder="Qty"
                      className="w-20"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 1)}
                    />
                    <Input 
                      type="number"
                      placeholder="Price"
                      className="w-28"
                      min="0"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                    />
                    <span className="w-24 text-right font-medium">
                      {invoiceForm.currency} {(item.quantity * item.unit_price).toFixed(2)}
                    </span>
                    {invoiceForm.line_items.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeLineItem(index)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount</Label>
                <Input 
                  type="number"
                  min="0"
                  step="0.01"
                  value={invoiceForm.discount}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, discount: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Tax Rate (%)</Label>
                <Input 
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={invoiceForm.tax_rate}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>{invoiceForm.currency} {calculateSubtotal().toFixed(2)}</span>
              </div>
              {invoiceForm.discount > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Discount:</span>
                  <span>-{invoiceForm.currency} {invoiceForm.discount.toFixed(2)}</span>
                </div>
              )}
              {invoiceForm.tax_rate > 0 && (
                <div className="flex justify-between">
                  <span>Tax ({invoiceForm.tax_rate}%):</span>
                  <span>{invoiceForm.currency} {(calculateSubtotal() * invoiceForm.tax_rate / 100).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg border-t mt-2 pt-2">
                <span>Total:</span>
                <span>{invoiceForm.currency} {calculateTotal().toFixed(2)}</span>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea 
                placeholder="Additional notes..."
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>

            <div>
              <Label>Terms</Label>
              <Textarea 
                placeholder="Payment terms..."
                value={invoiceForm.terms}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, terms: e.target.value }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateInvoice} disabled={savingInvoice}>
              {savingInvoice && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              {selectedInvoice && `Invoice ${selectedInvoice.invoice_number} - Balance: ${selectedInvoice.currency} ${(selectedInvoice.total - selectedInvoice.amount_paid).toFixed(2)}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Payment Date</Label>
              <Input 
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
              />
            </div>
            <div>
              <Label>Amount</Label>
              <Input 
                type="number"
                min="0"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={paymentForm.method} onValueChange={(v) => setPaymentForm(prev => ({ ...prev, method: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference / Receipt #</Label>
              <Input 
                placeholder="Optional"
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, reference: e.target.value }))}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea 
                placeholder="Optional notes..."
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handleAddPayment} disabled={savingPayment}>
              {savingPayment && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
